import { Pin, Plus, Search } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Spinner, StatusDot, statusLabel } from '../../components/ui';
import { shortTime } from '../../lib/format';
import { useConversations, useProjects, type ProjectDetail } from '../../lib/queries';
import { ui } from '../../lib/store';
import type { ConversationMode } from '@harness/shared';
import { modeMeta } from '../../lib/modes';
import { useCan, VIEWER_HINT } from '../../lib/role';

export default function Sidebar({
  project,
  mode,
  activeConvId,
  onNewConversation,
  drawer,
  onNavigate,
  disabled,
}: {
  project: ProjectDetail;
  /** Lists only this tab's conversations. */
  mode: ConversationMode;
  activeConvId?: string;
  onNewConversation: () => void;
  drawer: boolean;
  onNavigate: () => void;
  disabled?: boolean;
}) {
  const { data: projects = [] } = useProjects();
  const can = useCan();
  const { data: all = [], isLoading } = useConversations(project.id);
  const convs = all.filter((c) => c.mode === mode);
  const meta = modeMeta(mode);

  const recent = projects
    .filter((p) => !p.archived && (p.pinned || p.id === project.id || p.runningTurns > 0))
    .concat(projects.filter((p) => !p.archived && !p.pinned && p.id !== project.id).sort((a, b) => b.lastActiveAt.localeCompare(a.lastActiveAt)))
    .filter((p, i, a) => a.findIndex((x) => x.id === p.id) === i)
    .slice(0, 5);

  const where = project.server ? `${project.server.host || project.server.workingDir} · ${statusLabel(project.server.status)}` : 'No server connected';

  return (
    <aside className={`card ws-card sidebar ${drawer ? 'drawer' : ''}`} aria-label="Conversations">
      <div className="side-head">
        <span className="initial-tile" style={{ width: 40, height: 40, fontSize: 17, ...(project.color ? { background: project.color, color: '#fff' } : {}) }}>
          {project.name[0]?.toUpperCase()}
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span className="side-name ellipsis">{project.name}</span>
          <span className="muted ellipsis" style={{ display: 'block', fontSize: 12 }} title={where}>
            {where}
          </span>
        </span>
      </div>

      <div className="side-actions">
        <button className="btn btn-primary" style={{ flex: 1 }} onClick={onNewConversation} disabled={!can.edit || disabled} title={can.edit ? undefined : VIEWER_HINT}>
          <Plus className="icon" />
          {meta.newLabel}
        </button>
        <button className="btn btn-icon btn-secondary" title="Search (⌘K)" aria-label="Search" onClick={() => ui.set({ palette: true })}>
          <Search className="icon" style={{ fontSize: 15 }} />
        </button>
      </div>

      <nav className="side-scroll">
        <div className="micro side-label">{meta.listLabel}</div>
        {isLoading && (
          <div style={{ padding: '8px 10px' }}>
            <Spinner />
          </div>
        )}
        {!isLoading && convs.length === 0 && (
          <div className="faint" style={{ padding: '4px 10px 8px', fontSize: 13 }}>
            None yet. Start one above.
          </div>
        )}
        {convs.map((c) => (
          <Link key={c.id} to={`/p/${project.slug}/c/${c.id}`} className="side-row" aria-current={c.id === activeConvId} onClick={onNavigate}>
            {c.pinned && <Pin className="icon faint" style={{ fontSize: 11 }} />}
            <span className="ellipsis" style={{ flex: 1 }}>
              {c.title}
            </span>
            {c.status === 'running' && <Spinner className="accent-text" />}
            {c.mode === 'ticket' && c.ticket ? (
              <span className={`tag sm ${c.ticket.status === 'created' ? 'tag-done' : c.ticket.status === 'approved' ? 'tag-gate' : ''}`} title={c.ticket.jiraKey ?? undefined}>
                {c.ticket.status === 'created' ? c.ticket.jiraKey : c.ticket.status === 'approved' ? 'Approved' : 'Draft'}
              </span>
            ) : c.mode === 'wireframe' && c.jiraKey ? (
              <span className="tag sm">{c.jiraKey}</span>
            ) : (
              c.badge && <span className={`tag sm ${c.badge === 'Done' ? 'tag-done' : 'tag-gate'}`}>{c.badge}</span>
            )}
            <span className="time">{shortTime(c.updatedAt)}</span>
          </Link>
        ))}

        <div className="micro side-label" style={{ paddingTop: 18 }}>
          Pinned &amp; recent projects
        </div>
        {recent.map((p) => (
          <Link key={p.id} to={`/p/${p.slug}`} className="side-row" aria-current={p.id === project.id} onClick={onNavigate} style={{ gap: 10 }}>
            <StatusDot status={p.server?.status} />
            <span className="ellipsis" style={{ flex: 1 }}>
              {p.name}
            </span>
            {p.id !== project.id && p.runningTurns > 0 && <Spinner className="accent-text" />}
            {p.id !== project.id && p.pendingGates > 0 && p.runningTurns === 0 && <span className="tag tag-gate sm">Gate</span>}
          </Link>
        ))}
      </nav>
    </aside>
  );
}
